/* eslint-disable react-hooks/exhaustive-deps */
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Button, InputGroup, Modal, Spinner } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { toast } from 'react-toastify';
import { userService } from 'services/user.service';
import Select from 'react-select';
import { isNullOrUndefined } from 'common/data-util';
import { isEmailValidAndPresent, isPersonCode } from 'helpers/validators';
import { CustomerContactPerson } from 'models/contact-person';
import CustomerContactService from 'services/customer-contact.service';
import { InitContextProps, useAuthContext } from 'services/context/authentication.context';
import { SigningForm } from 'components/Modals/SigningModal/SigningForm';
import { SigningAction } from 'models/signing';
import { CustomerRole, CustomerRoleTranslations, CUSTOMER_ROLE_OPTIONS } from 'models/customer';

interface ModifyCustomerContactModalProps {
    handleModalHide: () => void;
    onSubmitSuccess: () => void;
    contact?: CustomerContactPerson;
    existingContactCodes?: string[]
}

interface ModifyCustomerContactModalForm {
    personalIdentificationCode?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
    role?: CustomerRole;
    canViewPin?: boolean;
}

export function ModifyCustomerContactModal(props: ModifyCustomerContactModalProps) {
    const { state }: InitContextProps = useAuthContext();
    const [loading, setLoading] = useState(false);
    const [validated, setValidated] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showAdditionalFields, setShowAdditionalFields] = useState(false);
    const [hasExistingUser, setHasExistingUser] = useState(false);
    const [showSigning, setShowSigning] = useState(false);

    const getRole = (contact?: CustomerContactPerson) => {
        if (!contact) return CustomerRole.WEB_ACCESS;
        if (contact.representative) return CustomerRole.REPRESENTATIVE;
        if (contact.webAdmin) return CustomerRole.WEB_ADMIN;
        return CustomerRole.WEB_ACCESS
    }

    const base = {
        personalIdentificationCode: props.contact?.personalIdentificationCode,
        phoneNumber: props.contact?.phone ?? props.contact?.mobileNumber,
        email: props.contact?.contactEmail,
        role: getRole(props.contact),
        canViewPin: props.contact?.canViewPin ?? false,
    };

    const [form, setForm] = useState<ModifyCustomerContactModalForm>(base);

    useEffect(() => {
        if (props.contact?.personalIdentificationCode) handleSearch();
    }, []);

    const requiresSigning = () => {
        const hadViewPin = props.contact?.canViewPin ?? false;
        const wantsViewPin = form.role === CustomerRole.WEB_ADMIN && (form.canViewPin ?? false);
        return wantsViewPin && !hadViewPin;
    };

    const handleSubmit = async (signingSessionId?: string) => {
        const contactPersonId = state.customer?.contactId;
        if (!contactPersonId || !state.customerId) return;

        setSaving(true);

        try {
            const isWebAdmin = form.role === CustomerRole.WEB_ADMIN;
            const isWebAccess = form.role === CustomerRole.WEB_ACCESS;

            if (isModify) {
                await CustomerContactService.updateContactPerson(props.contact!.id, contactPersonId, {
                    phoneNumber: form.phoneNumber!,
                    email: form.email!,
                    webAccess: isWebAccess,
                    webAdmin: isWebAdmin,
                    canViewPin: isWebAdmin && (form.canViewPin ?? false),
                    signingSessionId
                });

                toast.success('Kasutaja edukalt muudetud.');
            } else {
                await CustomerContactService.createContactPerson(contactPersonId, {
                    customerId: state.customerId,
                    personalIdentificationCode: form.personalIdentificationCode!,
                    firstName: form.firstName!,
                    lastName: form.lastName!,
                    phoneNumber: form.phoneNumber!,
                    email: form.email!,
                    webAccess: isWebAccess,
                    webAdmin: isWebAdmin,
                    canViewPin: isWebAdmin && (form.canViewPin ?? false),
                    signingSessionId
                });

                toast.success('Kasutaja edukalt lisatud.');
            }

            props.onSubmitSuccess();
            props.handleModalHide();
        } catch (e) {
            toast.error('Viga kasutaja salvestamisel.');
        } finally {
            setSaving(false);
        }
    }

    const handlePersonalCodeChange = (value: string) => {
        setForm({ personalIdentificationCode: value });
        setShowAdditionalFields(false);
        setHasExistingUser(false);
        setValidated(false);
    };

    const handleSearch = async () => {
        setValidated(true);

        if(!isPersonCode(form.personalIdentificationCode)) return;

        setValidated(false);

        try {
            setLoading(true);
            setHasExistingUser(false);

            const result = await userService.getUserByPersonalCode(form.personalIdentificationCode!);

            const base = { 
                firstName: result?.firstName,
                lastName: result?.lastName,
            };

            if (isModify) {
                setForm({
                    ...form,
                    ...base
                });
            } else {
                setForm({
                    ...form,
                    ...base,
                    phoneNumber: undefined,
                    email: undefined,
                    role: CustomerRole.WEB_ACCESS
                });
            }

            setHasExistingUser(true);
            setShowAdditionalFields(true);
        } catch (err) {
            console.error(err);
            const e : any = err;

            if (e?.message && e.message.includes("User not found")) {
                setShowAdditionalFields(true);
            } else {
                toast.error('Viga kasutaja otsimisel.');
            }
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        if (
            !isPersonCode(form.personalIdentificationCode) || 
            isNullOrUndefined(form.firstName) || 
            isNullOrUndefined(form.lastName) || 
            isNullOrUndefined(form.phoneNumber) || 
            !isEmailValidAndPresent(form.email) || 
            isNullOrUndefined(form.role)
        ) return false;
        return true;
    };

    const confirmSubmit = () => {
        setValidated(false);
        const isValidForm = validateForm();
        setValidated(true);
        if (isValidForm) {
            if (requiresSigning()) setShowSigning(true); 
            else handleSubmit();
        } else setSaving(false);
    };

    const onSigningSuccess = (signingSessionId: string) => {
        setShowSigning(false);
        handleSubmit(signingSessionId);
    };

    const isModify = !!props.contact;
    const isExistingContact = !isModify && !!props.existingContactCodes?.includes(form.personalIdentificationCode!);
    const submitLabel = isModify ? 'Muuda kasutajat' : 'Lisa kasutaja';

    return (
        <>
            <Modal
                show={true}
                size={"lg"}
                aria-labelledby="contained-modal-title-vcenter"
                centered
                onHide={props.handleModalHide}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isModify ? 'Kasutaja muutmine' : 'Kasutaja lisamine'}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {showSigning ? (
                        <SigningForm
                            action={SigningAction.GRANT_PIN_VIEW}
                            payload={{
                                personalIdentificationCode: form.personalIdentificationCode!,
                                fullName: `${form.firstName} ${form.lastName}`
                            }}
                            submitLabel={submitLabel}
                            savingLabel={isModify ? 'Kasutaja muutmine…' : 'Kasutaja lisamine…'}
                            onSuccess={onSigningSuccess}
                            onCancel={() => setShowSigning(false)}
                        />
                    ) : (
                    <Form noValidate validated={validated} onSubmit={(e: SyntheticEvent) => e.preventDefault()}
                          id={"card-form"}
                          className={'d-flex flex-column'}>
                        <Form.Group controlId={'form-personal-id-code'} className={'col-md-6 mb-3 d-flex align-items-center'}>
                            <div className="flex-grow-1">
                                <Form.Label>Isikukood</Form.Label>
                                <Form.Control
                                    required
                                    value={form.personalIdentificationCode}
                                    onChange={e => handlePersonalCodeChange(e.target.value)}
                                    disabled={isModify}
                                    type="number"
                                    placeholder="Isikukood"
                                    isInvalid={(validated && !isPersonCode(form.personalIdentificationCode)) || isExistingContact}
                                />
                                <Form.Control.Feedback className={'text-right'} type="invalid">
                                    { isExistingContact && 'Sellise isikukoodiga kasutaja on juba olemas!' }
                                    { validated && !isPersonCode(form.personalIdentificationCode) && 'Vigane isikukood!' }
                                </Form.Control.Feedback>
                            </div>
                            {
                                (!isModify || loading) && (
                                    <Button
                                        variant="primary"
                                        className="ml-3 mt-4"
                                        onClick={handleSearch}
                                        disabled={loading || saving || isExistingContact || isModify}
                                    >
                                        {loading ? <Spinner animation="border" size="sm" /> : 'Otsi'}
                                    </Button>
                                )
                            }
                        </Form.Group>
                        {showAdditionalFields && (
                            <>
                                <div className="d-flex">
                                    <Form.Group controlId={'form-first-name'} className={'col-md-6 mb-3'}>
                                        <Form.Label>Eesnimi</Form.Label>
                                        <Form.Control
                                            required
                                            value={form.firstName}
                                            onChange={e => setForm({ ...form, firstName: e.target.value })}
                                            disabled={saving || (hasExistingUser && !!form.firstName)}
                                            type="text"
                                            placeholder="Eesnimi"
                                        />
                                        <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId={'form-last-name'} className={'col-md-6 mb-3'}>
                                        <Form.Label>Perekonnanimi</Form.Label>
                                        <Form.Control
                                            required
                                            value={form.lastName}
                                            onChange={e => setForm({ ...form, lastName: e.target.value })}
                                            disabled={saving || (hasExistingUser && !!form.lastName)}
                                            type="text"
                                            placeholder="Perekonnanimi"
                                        />
                                        <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                                <div className="d-flex">
                                    <Form.Group controlId={'form-phone-number'} className={'col-md-6 mb-3'}>
                                        <Form.Label>Telefoninumber</Form.Label>
                                        <InputGroup className="col-12 p-0">
                                            <InputGroup.Prepend>
                                                <InputGroup.Text
                                                    id="phone-number-prefix"
                                                >
                                                    +372
                                                </InputGroup.Text>
                                            </InputGroup.Prepend>
                                            <Form.Control
                                                required
                                                value={form.phoneNumber}
                                                onChange={e => setForm({ ...form, phoneNumber: e.target.value })}
                                                disabled={loading}
                                                type="number"
                                                placeholder="Telefoninumber"
                                            />
                                            <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>
                                    <Form.Group controlId={'form-email'} className={'col-md-6 mb-3'}>
                                        <Form.Label>E-mail</Form.Label>
                                        <Form.Control
                                            required
                                            value={form.email}
                                            onChange={e => setForm({ ...form, email: e.target.value })}
                                            disabled={loading}
                                            type="email"
                                            placeholder="E-mail"
                                        />
                                        <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                                <div className='d-flex'>
                                    {
                                        form.role === CustomerRole.REPRESENTATIVE ? (
                                            <Form.Group controlId={'form-role'} className={'col-md-6'}>
                                                <Form.Label>Roll</Form.Label>
                                                <Form.Control
                                                    required
                                                    value={"Esindaja"}
                                                    disabled
                                                    type="text"
                                                />
                                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                            </Form.Group>
                                        ) : (
                                            <Form.Group controlId={'form-role'} className={'col-md-6'}>
                                                <Form.Label>Roll</Form.Label>
                                                <Select
                                                    options={CUSTOMER_ROLE_OPTIONS}
                                                    value={CUSTOMER_ROLE_OPTIONS.find(opt => opt.value === form.role) || null}
                                                    classNamePrefix={'select'}
                                                    className={'select-terminal ' + (isNullOrUndefined(form.role) && validated ? 'invalid' : '')}
                                                    onChange={(val: any) => setForm({
                                                        ...form,
                                                        role: val ? val.value : '',
                                                    })}
                                                    required
                                                    placeholder="Vali roll"
                                                />
                                                <Form.Control.Feedback className={'text-right'} type="invalid">Kohustuslik väli</Form.Control.Feedback>
                                                {
                                                    form.role === CustomerRole.WEB_ADMIN && (
                                                        <Form.Check
                                                            id='check-can-view-pin'
                                                            custom
                                                            type="checkbox"
                                                            label="PIN koodi vaatamine"
                                                            className='checkbox-small mt-2'
                                                            onChange={() => setForm({ ...form, canViewPin: !form.canViewPin })}
                                                            checked={form.canViewPin ?? false}
                                                        />
                                                    )
                                                }
                                            </Form.Group>
                                        )
                                    }
                                    {
                                        form.role !== undefined && (
                                            <Form.Group controlId={'form-role-permissions'} className={'col-md-6'}>
                                                <Form.Label>Lubatud tegevused ({CustomerRoleTranslations[form.role]})</Form.Label>
                                                { form.role === CustomerRole.WEB_ACCESS && (
                                                    <ul className="small mb-0">
                                                        <li>Saab ettevõtte alt sisse logida</li>
                                                        <li>Saab oma andmeid vaadata</li>
                                                    </ul>
                                                )}
                                                { form.role === CustomerRole.WEB_ADMIN && (
                                                    <ul className="small mb-0">
                                                        <li>Kõik Tavakasutaja õigused</li>
                                                        <li>Lepingute algatamine, lõpetamine ja allkirjastamine</li>
                                                        <li>Elektrilepingu paketi vahetamine</li>
                                                        <li>Taotluste algatamine, muutmine ja kustutamine</li>
                                                        <li>Pakkumuste vastuvõtmine</li>
                                                        <li>Tellimuste edastamine ja hinnapakkumiste küsimine</li>
                                                        <li>Arvete maksmine</li>
                                                        <li>Kaartide aktiveerimine (sh mass-aktiveerimine)</li>
                                                        <li>Kaartide kuulimiidi muutmine</li>
                                                        <li>Kaartide PIN-i vaatamine (kui on lubatud)</li>
                                                    </ul>
                                                )}
                                                { form.role === CustomerRole.REPRESENTATIVE && (
                                                    <ul className="small mb-0">
                                                        <li>Kõik Peakasutaja õigused</li>
                                                        <li>Kõik toimingud (kaardid, lepingud, taotlused, PIN-id)</li>
                                                        <li>Kasutajate haldamine (rollide ja õiguste muutmine)</li>
                                                    </ul>
                                                )}
                                            </Form.Group>
                                        )
                                    }
                                </div>
                            </>
                        )}
                    </Form>
                    )}
                </Modal.Body>
                { !showSigning && (
                    <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
                        <Button variant="outline-dark"
                                onClick={props.handleModalHide}
                                disabled={loading}
                                type="button"
                                className={"d-flex align-items-center"}>
                            <span className='px-2'>Loobu</span>
                        </Button>
                        <Button
                            variant="primary"
                            onClick={confirmSubmit}
                            disabled={saving}
                            type="button"
                            className="d-flex align-items-center"
                        >
                            <span className='px-2'>{ saving && <Spinner animation='border' className='mr-2' /> } { submitLabel }</span>
                        </Button>
                    </Modal.Footer>
                )}
            </Modal>
        </>
    );
}
